import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Modal } from '../components/Modal';
import type {
  AssignableLease,
  Property,
  ReconDetail,
  ReconLineRow,
  ReconListRow,
  ReconStatus,
} from '../types';

export const STATUS_META: Record<ReconStatus, { label: string; cls: string }> = {
  match: { label: 'Match', cls: 'ok' },
  rent_diff: { label: 'Rent differs', cls: 'danger' },
  fee_diff: { label: 'Fee differs', cls: 'warn' },
  rent_and_fee_diff: { label: 'Rent & fee differ', cls: 'danger' },
  ambiguous_match: { label: 'Review match', cls: 'escalate' },
  invoiced_not_in_system: { label: 'Code not found in Lease App', cls: 'danger' },
  in_system_not_invoiced: { label: 'Lease not found in CSV', cls: 'warn' },
};

// Worst-first, so discrepancies sit at the top of the report.
const STATUS_ORDER: ReconStatus[] = [
  'rent_and_fee_diff',
  'rent_diff',
  'fee_diff',
  'invoiced_not_in_system',
  'ambiguous_match',
  'in_system_not_invoiced',
  'match',
];

export const fmtMoney = (v: string | number | null): string =>
  v === null || v === '' ? '—' : Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
  );
};

export const StatusTag = ({ status }: { status: ReconStatus }) => {
  const m = STATUS_META[status];
  return <span className={`tag ${m.cls}`}>{m.label}</span>;
};

const UploadForm = ({ onDone }: { onDone: (id: number) => void }) => {
  const [csv, setCsv] = useState<File | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertyId, setPropertyId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.properties().then(setProperties).catch(() => setProperties([]));
  }, []);

  const submit = async () => {
    if (!csv || propertyId === '') return;
    setBusy(true);
    setError(null);
    try {
      let out = await api.uploadReconciliation(csv, propertyId);
      if (!out.ok) {
        const chosen = properties.find((p) => p.id === propertyId)?.name ?? out.mismatch.chosen;
        const proceed = window.confirm(
          `The CSV text refers to "${out.mismatch.detected.join(', ')}", ` +
            `but you selected "${chosen}". Upload against "${chosen}" anyway?`,
        );
        if (!proceed) return;
        out = await api.uploadReconciliation(csv, propertyId, true);
        if (!out.ok) return;
      }
      onDone(out.result.id);
      setCsv(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-section">
      <div className="form-grid">
        <label className="field">
          <span>Sage upload CSV</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setCsv(e.target.files?.[0] ?? null)}
          />
        </label>
        <label className="field">
          <span>Property</span>
          <select
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Select a property…</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="toolbar">
        <button
          className="primary"
          disabled={!csv || propertyId === '' || busy}
          onClick={() => void submit()}
        >
          {busy ? 'Reconciling…' : 'Reconcile & save'}
        </button>
        <span className="muted">
          Lines are matched to leases by their Sage code. Codes are set on each lease (Leases →
          Edit), fixed inline from a report line, or seeded via the{' '}
          <Link to="/reconciliation/rent-roll-check">rent roll check</Link>.
        </span>
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
};

const SummaryChips = ({ s }: { s: ReconListRow['summary'] }) => (
  <div className="toolbar" style={{ gap: 6, flexWrap: 'wrap' }}>
    <span className="tag ok">{s.matched} matched</span>
    {s.rentDiffs > 0 && <span className="tag danger">{s.rentDiffs} rent diff</span>}
    {s.feeDiffs > 0 && <span className="tag warn">{s.feeDiffs} fee diff</span>}
    {s.ambiguous > 0 && <span className="tag escalate">{s.ambiguous} review</span>}
    {s.invoicedNotInSystem > 0 && (
      <span className="tag danger">{s.invoicedNotInSystem} code not found</span>
    )}
    {s.inSystemNotInvoiced > 0 && (
      <span className="tag warn">{s.inSystemNotInvoiced} not in CSV</span>
    )}
  </div>
);

const Detail = ({ id, onBack }: { id: number; onBack: () => void }) => {
  const [data, setData] = useState<ReconDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [fixLine, setFixLine] = useState<ReconLineRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.reconciliation(id));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="page">Loading…</div>;
  if (!data) return <div className="page">Not found.</div>;

  const lines = [...data.lines].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
  );

  const remove = async () => {
    if (!confirm('Delete this reconciliation from the history?')) return;
    await api.deleteReconciliation(id);
    onBack();
  };

  const rerun = async () => {
    setRerunning(true);
    try {
      await api.rerunReconciliation(id);
      await load();
    } finally {
      setRerunning(false);
    }
  };

  return (
    <div className="page">
      <div className="toolbar">
        <button onClick={onBack}>← Back</button>
        <span className="spacer" />
        <button disabled={rerunning} onClick={() => void rerun()} title="Recompute this run's stored CSV against the current leases and Sage codes">
          {rerunning ? 'Re-running…' : '↻ Re-run against current data'}
        </button>
        <a href={`/api/reconciliations/${id}/csv`}>Download CSV</a>
        <button className="danger" onClick={() => void remove()}>
          Delete
        </button>
      </div>

      <h1>
        {data.propertyNameRaw ?? 'Reconciliation'}
        {data.periodLabel ? ` — ${data.periodLabel}` : ''}
      </h1>
      <p className="muted">
        Uploaded {fmtTime(data.createdAt)} · CSV: {data.csvFilename ?? '—'} · Rent roll:{' '}
        {data.rentrollFilename ?? '—'}
      </p>
      <SummaryChips s={data.summary} />
      {data.summary.propertyTextWarning && (
        <div className="error">
          ⚠ This CSV's text refers to “{data.summary.propertyTextWarning}”, which did not match{' '}
          {data.propertyNameRaw ?? 'the chosen property'}. It was uploaded anyway.
        </div>
      )}
      <p className="muted">
        CSV rent total {fmtMoney(String(data.summary.totalCsvRent))} · System active-rent total{' '}
        {fmtMoney(String(data.summary.totalSystemRent))}
      </p>

      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Status</th>
              <th>Code</th>
              <th>Tenant</th>
              <th style={{ textAlign: 'right' }}>CSV rent</th>
              <th style={{ textAlign: 'right' }}>System rent</th>
              <th style={{ textAlign: 'right' }}>Rent diff</th>
              <th style={{ textAlign: 'right' }}>CSV fee</th>
              <th style={{ textAlign: 'right' }}>Sys fee</th>
              <th style={{ textAlign: 'right' }}>Fee diff</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <LineRow key={l.id} l={l} onFix={setFixLine} />
            ))}
          </tbody>
        </table>
      </div>

      {fixLine && (
        <FixCodeModal
          reconId={id}
          line={fixLine}
          onClose={() => setFixLine(null)}
          onDone={async () => {
            setFixLine(null);
            await load();
          }}
        />
      )}
    </div>
  );
};

const Diff = ({ v }: { v: string | null }) => {
  if (v === null) return <span className="muted">—</span>;
  const n = Number(v);
  if (n === 0) return <span className="muted">0.00</span>;
  return <strong style={{ color: '#b00020' }}>{n > 0 ? '+' : ''}{fmtMoney(v)}</strong>;
};

// A line is fixable in-app when either the CSV code matches no lease (assign
// it to the right lease(s)) or active lease(s) carry no code at all (give
// them one). Coded leases that weren't invoiced are real Sage-side drift —
// no button.
const fixAction = (l: ReconLineRow): string | null => {
  if (l.status === 'invoiced_not_in_system' && l.custAccount) return 'Assign code…';
  if (
    l.status === 'in_system_not_invoiced' &&
    !l.custAccount &&
    (l.detail?.systemLeaseIds.length ?? 0) > 0
  )
    return 'Set code…';
  return null;
};

const LineRow = ({ l, onFix }: { l: ReconLineRow; onFix: (l: ReconLineRow) => void }) => {
  const notes = l.detail?.notes ?? [];
  const candidates = l.detail?.candidates ?? [];
  const action = fixAction(l);
  return (
    <>
      <tr>
        <td>
          <StatusTag status={l.status} />
        </td>
        <td>{l.custAccount ?? '—'}</td>
        <td>
          {l.matchedTenantName ?? l.excelTenantName ?? '—'}
          {l.matchScore !== null && (
            <span className="muted"> ({l.matchMethod} {l.matchScore})</span>
          )}
        </td>
        <td style={{ textAlign: 'right' }}>{fmtMoney(l.csvRent)}</td>
        <td style={{ textAlign: 'right' }}>{fmtMoney(l.systemRent)}</td>
        <td style={{ textAlign: 'right' }}>
          <Diff v={l.rentDiff} />
        </td>
        <td style={{ textAlign: 'right' }}>{fmtMoney(l.csvManagementFee)}</td>
        <td style={{ textAlign: 'right' }}>{fmtMoney(l.systemManagementFee)}</td>
        <td style={{ textAlign: 'right' }}>
          <Diff v={l.managementFeeDiff} />
        </td>
        <td>{action && <button onClick={() => onFix(l)}>{action}</button>}</td>
      </tr>
      {(notes.length > 0 || (l.status === 'ambiguous_match' && candidates.length > 0)) && (
        <tr>
          <td colSpan={10} className="muted" style={{ paddingTop: 0 }}>
            {notes.map((n, i) => (
              <div key={i}>· {n}</div>
            ))}
            {l.status === 'ambiguous_match' && candidates.length > 0 && (
              <div>
                · Candidates:{' '}
                {candidates.map((cd) => `${cd.tenantName} (${cd.score.toFixed(2)})`).join(', ')}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// Assigns a Sage code to lease(s) straight from a reconciliation line, then
// re-runs the reconciliation so the line reflects the fix. Two entry points:
// a CSV code matching no lease (tick the lease(s) it covers) or code-less
// lease(s) (type the code) — both end in the same applyCodes → rerun flow.
const FixCodeModal = ({
  reconId,
  line,
  onClose,
  onDone,
}: {
  reconId: number;
  line: ReconLineRow;
  onClose: () => void;
  onDone: () => Promise<void>;
}) => {
  const pickLeases = line.status === 'invoiced_not_in_system';
  const [leases, setLeases] = useState<AssignableLease[] | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(pickLeases ? [] : (line.detail?.systemLeaseIds ?? [])),
  );
  const [code, setCode] = useState(line.custAccount ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .reconAssignableLeases(reconId)
      .then(setLeases)
      .catch(() => setLeases([]));
  }, [reconId]);

  const trimmedCode = code.trim().toUpperCase();
  const chosen = (leases ?? []).filter((l) => selected.has(l.leaseId));
  const overwriting = chosen.filter((l) => l.sageCode && l.sageCode !== trimmedCode);
  // Same code already on an unticked lease of a *different* tenant: almost
  // certainly a mistake (one Sage account = one customer), but not blocked —
  // duplicate tenant records exist.
  const chosenTenantIds = new Set(chosen.map((l) => l.tenantId));
  const foreignHolders = (leases ?? []).filter(
    (l) =>
      !selected.has(l.leaseId) &&
      l.sageCode === trimmedCode &&
      trimmedCode !== '' &&
      !chosenTenantIds.has(l.tenantId),
  );

  const q = search.trim().toLowerCase();
  const filtered = (leases ?? []).filter(
    (l) =>
      l.tenantName.toLowerCase().includes(q) || (l.unit ?? '').toLowerCase().includes(q),
  );

  const toggle = (leaseId: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(leaseId) ? next.delete(leaseId) : next.add(leaseId);
      return next;
    });

  const apply = async () => {
    if (selected.size === 0 || !trimmedCode) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.applyCodes(
        [...selected].map((leaseId) => ({ leaseId, code: trimmedCode })),
      );
      if (res.failed.length > 0) {
        setError(res.failed.map((f) => `lease #${f.leaseId}: ${f.error}`).join('; '));
        return;
      }
      await api.rerunReconciliation(reconId);
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'assigning the code failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={
        pickLeases
          ? `Assign code ${line.custAccount} to lease(s)`
          : `Set Sage code for ${line.matchedTenantName ?? 'lease(s)'}`
      }
      onClose={onClose}
    >
      {pickLeases ? (
        <p className="muted">
          The CSV invoiced {fmtMoney(line.csvRent)} against code {line.custAccount}, but no active
          lease holds that code. Tick the lease(s) this Sage account covers — the code is saved on
          each ticked lease and the reconciliation is re-run.
        </p>
      ) : (
        <p className="muted">
          {line.matchedTenantName ?? 'This tenant'} has active lease(s) totalling{' '}
          {fmtMoney(line.systemRent)} with no Sage code, so the monthly CSV can never match them.
          Enter the Sage customer code (untick any lease billed under a different account) — it is
          saved on the ticked lease(s) and the reconciliation is re-run.
        </p>
      )}

      {!pickLeases && (
        <div className="field">
          <label>Sage code</label>
          <input
            placeholder="AP####"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
        </div>
      )}

      <input
        className="search"
        placeholder="Search tenant or unit…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8, width: '100%' }}
      />
      <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
        <table className="grid">
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th>Tenant</th>
              <th>Unit</th>
              <th style={{ textAlign: 'right' }}>Monthly rent</th>
              <th>Current code</th>
            </tr>
          </thead>
          <tbody>
            {leases === null ? (
              <tr>
                <td colSpan={5} className="muted">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted">
                  No active leases match.
                </td>
              </tr>
            ) : (
              filtered.map((l) => (
                <tr
                  key={l.leaseId}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggle(l.leaseId)}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(l.leaseId)}
                      onChange={() => toggle(l.leaseId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>{l.tenantName}</td>
                  <td className="muted">{l.unit ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {fmtMoney(l.systemRent)}
                    {l.currency ? ` ${l.currency}` : ''}
                  </td>
                  <td className="muted">{l.sageCode ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {overwriting.length > 0 && (
        <div className="error" style={{ marginTop: 8 }}>
          ⚠ {overwriting.map((l) => `${l.tenantName} ${l.unit ?? ''} (${l.sageCode})`).join(', ')}{' '}
          already hold a different code — saving replaces it with {trimmedCode}.
        </div>
      )}
      {foreignHolders.length > 0 && (
        <div className="error" style={{ marginTop: 8 }}>
          ⚠ Code {trimmedCode} is already on{' '}
          {foreignHolders.map((l) => `${l.tenantName} ${l.unit ?? ''}`).join(', ')} — a Sage
          account belongs to one customer, so sharing it across tenants is probably wrong.
        </div>
      )}
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose}>Cancel</button>
        <button
          className="primary"
          disabled={busy || selected.size === 0 || !trimmedCode}
          onClick={() => void apply()}
        >
          {busy ? 'Saving…' : `Save code on ${selected.size} lease${selected.size === 1 ? '' : 's'} & re-run`}
        </button>
      </div>
    </Modal>
  );
};

export const ReconciliationsPage = () => {
  const [rows, setRows] = useState<ReconListRow[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.reconciliations());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (selected !== null) {
    return (
      <Detail
        id={selected}
        onBack={() => {
          setSelected(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="page">
      <h1>Sage Reconciliation</h1>
      <p className="muted">
        Upload the Sage invoice CSV and pick its property. Each run is saved and compared, at
        upload time, against the rent our system computes for the matched leases (matched by each
        lease's Sage code — one code can cover several units).
      </p>

      <UploadForm onDone={(id) => setSelected(id)} />

      <h2>History</h2>
      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No reconciliations yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Uploaded</th>
                <th>Property</th>
                <th>Period</th>
                <th>CSV</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(r.id)}
                >
                  <td>{fmtTime(r.createdAt)}</td>
                  <td>{r.propertyNameRaw ?? '—'}</td>
                  <td>{r.periodLabel ?? '—'}</td>
                  <td>{r.csvFilename ?? '—'}</td>
                  <td>
                    <SummaryChips s={r.summary} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
